import { inject, Injectable } from '@angular/core';
import { SupplierService } from '@vdms-hq/api-contract';
import {
  ConnectableDataSource,
  FilterableDataSource,
  LoadableDataSource,
  PageableDataSource,
  RefreshService,
  SelectableDataSource,
  Selection,
  SortableDataSource,
  SortBy,
  SortDirection,
  SortEvent,
  SuppliersListOption,
} from '@vdms-hq/shared';
import { BehaviorSubject, combineLatest, debounceTime, map, Observable, shareReplay, switchMap, tap } from 'rxjs';
import { RouterParamsPagination } from '@vdms-hq/view-settings';

@Injectable({ providedIn: 'root' })
export class SuppliersDatasourceService
  extends RouterParamsPagination
  implements
    ConnectableDataSource<SuppliersListOption>,
    SelectableDataSource<SuppliersListOption>,
    LoadableDataSource,
    PageableDataSource,
    SortableDataSource,
    FilterableDataSource
{
  private readonly supplierService = inject(SupplierService);
  private refreshService = inject(RefreshService);

  text$ = new BehaviorSubject<string>('');
  emptyResults$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  // Sort keys are the backend order-by values, carried on each column's `sortObjectPath`
  // (GET /suppliers supports only supplier.createdAt / supplier.updatedAt /
  // supplierCreatedBy.displayName / supplierModifiedBy.displayName).
  sortBy$ = new BehaviorSubject<SortBy>(null);
  sortDirection$ = new BehaviorSubject<SortDirection>('');
  selection!: Selection<SuppliersListOption>;
  allData$: Observable<SuppliersListOption[]> = combineLatest([
    this.pageIndex$,
    this.pageSize$,
    this.text$,
    this.sortBy$,
    this.sortDirection$,
    this.refreshService.refresh$,
  ]).pipe(
    tap(() => this.isLoading$.next(true)),
    debounceTime(500),
    switchMap(([pageIndex, pageSize, text, sortBy, sortDirection]) =>
      this.supplierService.getPaginatedSuppliers(
        { text, perPage: pageSize.toString(), page: pageIndex.toString() },
        // No direction (sort cleared) → send neither header so the backend applies its default order.
        sortDirection ? (sortBy ?? undefined) : undefined,
        sortDirection || undefined,
      ),
    ),
    tap((response) => {
      this.isLoading$.next(false);
      if (!response) {
        this.emptyResults$.next(true);
      }
      this.total$.next(response?.total ?? 0);
    }),
    map((response) =>
      response.data.map((supplier) => ({
        ...supplier,
        emails: 'email' in supplier ? ([supplier.email] as string[]) : supplier.emails,
      })),
    ),
    // table-advanced subscribes connection$ twice (row binding + code); a cold stream here
    // means one GET /suppliers per subscriber.
    shareReplay({ bufferSize: 1, refCount: true }),
  );
  connection$ = this.allData$;

  total$ = new BehaviorSubject(0);

  applyFilter(value: string, fields?: string[]) {
    if (value?.length < 3 && value?.length !== 0) {
      return;
    } else if (value?.length === 0) {
      this.text$.next('');
    } else {
      this.text$.next(value);
    }
    this.changePageIndex$.next(0);
    this.navigateToFirstPage();
  }

  sortChange($event: SortEvent) {
    this.sortBy$.next($event.active);
    this.sortDirection$.next($event.direction);
    // A new sort re-queries from the first page so the top rows reflect the new order.
    this.changePageIndex$.next(0);
    this.navigateToFirstPage();
  }
}
